import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "~/components/ui/button";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";

interface AdminPageProps {
  title: string;
  description?: ReactNode;
  // Sits beside the title. Sized for a `Badge`, not prose.
  titleAdornment?: ReactNode;
  // For a detail page beneath a list.
  back?: { href: string; label: string };
  actions?: ReactNode;
  // Off for the legacy admin app, which spans full width.
  constrainWidth?: boolean;
  // For a page whose content scrolls itself rather than the page.
  fillHeight?: boolean;
  children: ReactNode;
}

/**
 * The frame every Admin page shares — column width, padding, rhythm, heading.
 *
 * Nextgen pages put their controls in the app header via `useNextGenHeader` and
 * leave `actions` empty; the legacy admin app has no such header, so it renders
 * them here.
 */
export const AdminPage = ({
  title,
  description,
  titleAdornment,
  back,
  actions,
  constrainWidth = false,
  fillHeight = false,
  children,
}: AdminPageProps) => (
  <div
    className={cn("flex w-full flex-col gap-6 p-6", fillHeight ? "h-full" : "min-h-full")}
    style={constrainWidth ? { maxWidth: NEXTGEN_MAX_CONTENT_WIDTH, margin: "0 auto" } : undefined}
  >
    <header className="flex shrink-0 items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-4">
        {back && (
          <Button variant="ghost" size="icon" asChild>
            <Link to={back.href} aria-label={back.label}>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
        )}
        <div className="min-w-0 space-y-1">
          <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
            {title}
            {titleAdornment}
          </h1>
          {description && <p className="text-lg text-muted-foreground">{description}</p>}
        </div>
      </div>
      {actions}
    </header>
    {fillHeight ? <div className="flex min-h-0 flex-1 flex-col gap-6">{children}</div> : children}
  </div>
);
